import { axiosInstance } from '@/utils/libs/axios';
import { useKeycloak } from '@react-keycloak/web';
import React, { useEffect, useState } from 'react';
import {
  ProtectedRouteProvider,
  useProtectedRoute,
} from './ProtectedRouteContext';
import { WithPublic } from './WithPublic';
import { Loading } from '@/components/Loading';

type ProtectedRouteProps = {
  layout: React.FC;
  isPublic?: boolean;
};

const ProtectedRouteComponent: React.FC<ProtectedRouteProps> = ({
  layout: Layout,
  isPublic = false,
}) => {
  const { keycloak, initialized } = useKeycloak();
  const { setIsPublic } = useProtectedRoute();
  const [isInitializing, setIsInitializing] = useState(true);

  useEffect(() => {
    setIsPublic(isPublic);

    // Keep routed components unmounted until the request interceptors are
    // installed. TableLowcode and menu queries can request data immediately
    // on mount, so rendering the layout first would send those requests
    // without the Keycloak token.
    setIsInitializing(true);

    if (!initialized) {
      return;
    }

    const requestInterceptor = axiosInstance.interceptors.request.use(
      async config => {
        if (isPublic) {
          config.headers['X-Public'] = 'True';
          return config;
        }

        if (keycloak.authenticated) {
          try {
            // Access token Keycloak hanya berlaku lima menit. Tanpa refresh di
            // sini, request tetap dikirim dengan token kedaluwarsa dan aplikasi
            // terlihat seolah sesi masih aktif.
            await keycloak.updateToken(30);
          } catch {
            keycloak.login();
            throw new Error('Session expired');
          }

          config.headers.Authorization = `Bearer ${keycloak.token}`;
        }

        return config;
      },
    );

    const responseInterceptor = axiosInstance.interceptors.response.use(
      response => response,
      error => {
        // Sesi sudah tidak diterima server: arahkan ke Sign In daripada
        // membiarkan halaman menampilkan data lama atau menyimpan tanpa user.
        if (!isPublic && error?.response?.status === 401) {
          keycloak.login();
        }

        return Promise.reject(error);
      },
    );

    if (!isPublic && !keycloak.authenticated) {
      keycloak.login();
    }

    setIsInitializing(false);

    // Interceptor didaftarkan ulang setiap effect berjalan, jadi harus dilepas.
    // Kalau tidak, interceptor rute publik ikut terbawa ke rute biasa dan
    // menempelkan header X-Public pada seluruh request berikutnya.
    return () => {
      axiosInstance.interceptors.request.eject(requestInterceptor);
      axiosInstance.interceptors.response.eject(responseInterceptor);
    };
  }, [initialized, isPublic, keycloak, setIsPublic]);

  // Tampilkan loader hingga `isPublic` selesai diatur
  if (isInitializing) {
    return <Loading />;
  }

  // Jika rute bersifat publik, langsung render layout.
  if (isPublic) {
    const children = WithPublic(Layout)({ isPublic: true });
    return children;
  }

  if (keycloak.authenticated) {
    return <Layout />;
  }

  return <Loading />;
};

export const ProtectedRoute: React.FC<ProtectedRouteProps> = props => (
  <ProtectedRouteProvider>
    <ProtectedRouteComponent {...props} />
  </ProtectedRouteProvider>
);
